import { getCountryConfig } from "@/lib/server/actions/get-country-config";
import { getScottApiClient } from "./scott-api/get-scott-api-client";
import { getScottApiClientForShopper } from "./scott-api/get-shopper-scott-api-client";

/**
 * Commerce context for catalogue reads, authenticated as the shopper whose OCC
 * token is supplied so SAP applies that shopper's promotions.
 *
 * Unlike {@link getCommerceContext} this does not resolve the base store —
 * catalogue calls do not need it, and skipping it avoids a second round trip
 * per product request.
 */
export async function getShopperCommerceContext(params: {
  country?: string;
  shopperToken?: string;
}) {
  const [countryConfig, shopper] = await Promise.all([
    getCountryConfig(params.country),
    getScottApiClientForShopper(params.shopperToken),
  ]);

  if (!countryConfig?.siteUid) {
    throw new Error("BaseSiteID not found in country configuration");
  }

  return {
    scottClient: shopper.client,
    baseSiteId: countryConfig.siteUid,
    isPersonalised: shopper.isPersonalised,
  };
}

export async function getCommerceContext(country?: string) {
  const [countryConfig, scottClient] = await Promise.all([
    getCountryConfig(country),
    getScottApiClient(),
  ]);

  if (!countryConfig?.siteUid) {
    throw new Error("BaseSiteID not found in country configuration");
  }

  const baseSiteId = countryConfig.siteUid;
  const baseStoreUid = baseSiteId.replaceAll("_", "-");

  const baseStoreResult = await scottClient
    .GET("/commerce/v1/{baseSiteId}/basestores/{baseStoreUid}", {
      params: {
        path: {
          baseSiteId,
          baseStoreUid,
        },
      },
      next: { revalidate: 86_400 },
    })
    .catch(() => {});

  return {
    scottClient,
    baseSiteId,
    baseStoreUid,
    baseStore: baseStoreResult?.data?.data,
  };
}
